import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Search, Download, Trash2, Calendar, DollarSign, Award, ArrowUpRight, Target } from "lucide-react";

export default function SubmissionsDashboard() {
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPain, setFilterPain] = useState("All");

  useEffect(() => {
    const loadLeads = () => {
      const stored = JSON.parse(localStorage.getItem("vph_captured_leads") || "[]");
      setLeads(stored);
    };
    loadLeads();
    
    // Add event listener to refresh leads if user adds one
    window.addEventListener("storage", loadLeads);
    return () => window.removeEventListener("storage", loadLeads);
  }, []);

  const clearLeads = () => {
    if (window.confirm("Are you sure you want to delete all simulation leads?")) {
      localStorage.setItem("vph_captured_leads", "[]");
      setLeads([]);
      toast.success("Simulation leads cleared!");
    }
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = 
      (lead.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.phone || "").includes(searchTerm);
    
    const matchesFilter = filterPain === "All" || (lead.painPoint || "").includes(filterPain);
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-800 pb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight" data-testid="dashboard-title">
              VPH Funnel leads Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Real-time submission logs captured from all 4 competing high-conversion landing pages.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={clearLeads}
              disabled={leads.length === 0}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 text-rose-500 rounded-xl transition-all disabled:opacity-50"
              data-testid="clear-leads-btn"
            >
              <Trash2 className="h-4 w-4" /> Clear All
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-2xl flex items-center justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">Total Submissions</span>
              <span className="text-3xl font-black mt-1 block" data-testid="stat-total-leads">{leads.length}</span>
            </div>
            <div className="p-3 rounded-xl bg-indigo-500/10 text-indigo-500">
              <Users className="h-6 w-6" />
            </div>
          </div>
          
          <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-2xl flex items-center justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">Resume Pain leads</span>
              <span className="text-3xl font-black mt-1 block">{leads.filter(l => (l.painPoint || "").includes("Resume")).length}</span>
            </div>
            <div className="p-3 rounded-xl bg-blue-500/10 text-blue-500">
              <Award className="h-6 w-6" />
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-2xl flex items-center justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">Interview Fear leads</span>
              <span className="text-3xl font-black mt-1 block">{leads.filter(l => (l.painPoint || "").includes("Interview")).length}</span>
            </div>
            <div className="p-3 rounded-xl bg-rose-500/10 text-rose-500">
              <Target className="h-6 w-6" />
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-2xl flex items-center justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">Silver Memberships Locked</span>
              <span className="text-3xl font-black mt-1 block text-amber-500">{leads.filter(l => (l.sourcePage || "").includes("silver") || (l.painPoint || "").includes("Silver")).length}</span>
            </div>
            <div className="p-3 rounded-xl bg-amber-500/10 text-amber-500">
              <DollarSign className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-slate-900/40 p-4 border border-slate-800/60 rounded-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search by candidate name, email, or phone..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="leads-search-input"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Filter Pain</span>
            <select
              className="px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-amber-500"
              value={filterPain}
              onChange={(e) => setFilterPain(e.target.value)}
              data-testid="leads-filter-select"
            >
              <option value="All">All Categories</option>
              <option value="Resume">Resume Pain</option>
              <option value="Interview">Interview Fear</option>
              <option value="Experience">Experience Gap</option>
              <option value="Confusion">Career Confusion</option>
            </select>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl overflow-hidden">
          {filteredLeads.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-3 stroke-1" />
              <p className="font-bold text-slate-300">No matching leads found</p>
              <p className="text-sm mt-1">Try simulating a sign-up from any of the landing pages first!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-950 border-b border-slate-800/60 text-slate-400 text-xs font-black uppercase tracking-wider">
                    <th className="p-4 sm:px-6">Candidate Details</th>
                    <th className="p-4">Struggle/Page</th>
                    <th className="p-4">Exp & Target</th>
                    <th className="p-4">Submitted At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40 text-sm">
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-800/10 transition-colors" data-testid="lead-row">
                      <td className="p-4 sm:px-6">
                        <div className="font-bold text-white text-base">{lead.name}</div>
                        <div className="text-slate-400 text-xs mt-0.5">{lead.email}</div>
                        <div className="text-slate-500 text-xs">{lead.phone}</div>
                      </td>
                      <td className="p-4">
                        <span className="inline-block px-2.5 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-xs">
                          {lead.painPoint}
                        </span>
                        <div className="text-[11px] text-slate-500 mt-1">
                          Source: <span className="font-mono">{lead.sourcePage}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-slate-300 font-semibold">{lead.experience}</div>
                        <div className="text-amber-500 text-xs font-black mt-0.5">{lead.targetSalary}</div>
                      </td>
                      <td className="p-4 text-slate-400 font-mono text-xs">
                        {new Date(lead.timestamp).toLocaleDateString()} {new Date(lead.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
